import { KPISummary } from '@/types'
import { formatCurrency, formatCompactNumber } from '@/utils/formatters'
import { ICON_PATHS, RawIcon } from '@/components/ui'
import { useT } from '@/i18n/useT'

interface KPICardsProps {
  kpi: KPISummary
  isLoading?: boolean
  showTax?: boolean
}

type Tone = 'accent' | 'ok' | 'warn' | 'muted'

const TONES: Record<Tone, { fg: string; bg: string }> = {
  accent: { fg: 'var(--accent)', bg: 'var(--accentSoft)' },
  ok: { fg: 'var(--ok)', bg: 'var(--okSoft)' },
  warn: { fg: 'var(--warn)', bg: 'var(--warnSoft)' },
  muted: { fg: 'var(--muted)', bg: 'var(--line2)' },
}

interface KPIDef {
  title: string
  value: string
  subtitle: string
  icon: string
  tone: Tone
}

function KPICard({ def }: { def: KPIDef }) {
  const tone = TONES[def.tone]
  return (
    <div
      style={{
        // Capped so a short last row keeps tile proportions instead of two
        // cards stretching across the full width.
        flex: '1 1 200px',
        maxWidth: 340,
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: tone.bg,
            color: tone.fg,
            flexShrink: 0,
          }}
        >
          <RawIcon path={def.icon} size={20} />
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '-0.5px',
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {def.value}
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 12 }}>
        {def.title}
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{def.subtitle}</div>
    </div>
  )
}

/**
 * The KPI strip. Icon glyphs and tone mapping come from the design mockup's
 * kpiDefs, which lines up one-to-one with the KPIs calculateKPIs() produces.
 */
export default function KPICards({ kpi, isLoading, showTax = false }: KPICardsProps) {
  const { t } = useT()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {Array.from({ length: 9 }, (_, i) => (
          <div
            key={i}
            style={{
              flex: '1 1 200px',
              height: 118,
              background: 'var(--hover)',
              borderRadius: 12,
            }}
          />
        ))}
      </div>
    )
  }

  const defs: KPIDef[] = [
    {
      title: t('Outstanding'),
      value: formatCompactNumber(kpi.outstandingAmount),
      subtitle: `${kpi.outstandingCount} ${t('unpaid')} · ${formatCurrency(kpi.outstandingAmount)}`,
      icon: ICON_PATHS.clock[0],
      tone: 'accent',
    },
    {
      title: t('Overdue'),
      value: formatCompactNumber(kpi.overdueAmount),
      subtitle: `${kpi.overdueCount} ${t('overdue')} · ${formatCurrency(kpi.overdueAmount)}`,
      icon: ICON_PATHS.warning[0],
      tone: 'warn',
    },
    {
      title: t('Due soon (7d)'),
      value: formatCompactNumber(kpi.dueSoonAmount),
      subtitle: `${kpi.dueSoonCount} ${t('due soon')} · ${formatCurrency(kpi.dueSoonAmount)}`,
      icon: ICON_PATHS.calendar[0],
      tone: 'muted',
    },
    {
      title: t('Approved'),
      value: String(kpi.approvedCount),
      subtitle: t('Approved for payment'),
      icon: ICON_PATHS.check[0],
      tone: 'ok',
    },
    {
      title: t('Pending'),
      value: String(kpi.pendingCount),
      subtitle: t('Open + in review'),
      icon: ICON_PATHS.list[0],
      tone: 'accent',
    },
    {
      title: t('Total net'),
      value: formatCompactNumber(kpi.totalNetto),
      subtitle: formatCurrency(kpi.totalNetto),
      icon: ICON_PATHS.bolt[0],
      tone: 'accent',
    },
    {
      title: t('Total gross'),
      value: formatCompactNumber(kpi.totalBrutto),
      subtitle: formatCurrency(kpi.totalBrutto),
      icon: ICON_PATHS.trend[0],
      tone: 'ok',
    },
    {
      title: t('Invoices'),
      value: String(kpi.invoiceCount),
      subtitle: t('Total records'),
      icon: ICON_PATHS.book[0],
      tone: 'muted',
    },
    {
      title: t('Suppliers'),
      value: String(kpi.supplierCount),
      subtitle: t('Unique suppliers'),
      icon: ICON_PATHS.building[0],
      tone: 'accent',
    },
    ...(showTax
      ? ([
          {
            title: t('VAT amount'),
            value: formatCompactNumber(kpi.totalVatAmount),
            subtitle: formatCurrency(kpi.totalVatAmount),
            icon: ICON_PATHS.bank[0],
            tone: 'muted',
          },
          {
            title: t('Tax-free amount'),
            value: formatCompactNumber(kpi.totalTaxFreeAmount),
            subtitle: formatCurrency(kpi.totalTaxFreeAmount),
            icon: ICON_PATHS.box[0],
            tone: 'warn',
          },
          {
            title: t('Taxable net'),
            value: formatCompactNumber(kpi.totalTaxableNetAmount),
            subtitle: formatCurrency(kpi.totalTaxableNetAmount),
            icon: ICON_PATHS.trend[0],
            tone: 'ok',
          },
        ] satisfies KPIDef[])
      : []),
  ]

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {defs.map((def) => (
        <KPICard key={def.title} def={def} />
      ))}
    </div>
  )
}
